import React, { useState, useRef, useEffect } from 'react';
import Services from '../../../services/Services';
import { AttendeeInfo } from '../../../types';
import Card from '../../ui/Card/Card';
import Button from '../../ui/Button/Button';
import { isValidEmail, isValidCardNumber, isValidCVV, isValidPhoneNumberWithCountry, isValidPhoneNumberForCountry } from '../../../utils/validators';
import { countryPhoneCodes, CountryPhoneCode, getMaxLengthForCountry } from '../../../utils/CountryPhoneCodes';
import CountryCodeSelector from '../../ui/CountryCodeSelector/CountryCodeSelector';
import { useAutofillSync } from '../../../hooks/useAutofillSync';
import styles from './PaymentForm.module.css';

type CardField = 'cardHolderName' | 'cardNumber' | 'expireMonth' | 'expireYear' | 'cvc' | 'gsmNumber';
type BillingField = 'identityNo' | 'address' | 'city' | 'country' | 'postalCode' | 'taxCode';
type PaymentField = CardField | BillingField;

interface PaymentFormProps {
  attendees: AttendeeInfo[];
  totalPrice: number;
  currency: string;
  userID: string;
  userInfo: {
    name: string;
    lastname: string;
    email: string;
    phone: string;
  };
  onPaymentSuccess: (paymentID: string, billing: Record<string, any>) => void;
  onPaymentError: (error: string) => void;
  getTranslation: (key: string) => string;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  attendees,
  totalPrice,
  currency,
  userID,
  userInfo,
  onPaymentSuccess,
  onPaymentError,
  getTranslation
}) => {
  const [cardData, setCardData] = useState({
    cardHolderName: '',
    cardNumber: '',
    expireMonth: '',
    expireYear: '',
    cvc: '',
    gsmNumber: ''
  });
  const [billingData, setBillingData] = useState({
    identityNo: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    taxCode: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [show3DS, setShow3DS] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('+90'); // Default to Turkey
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fieldRefs = useRef<Record<PaymentField, HTMLInputElement | HTMLSelectElement | null>>({
    cardHolderName: null,
    cardNumber: null,
    expireMonth: null,
    expireYear: null,
    cvc: null,
    gsmNumber: null,
    identityNo: null,
    address: null,
    city: null,
    country: null,
    postalCode: null,
    taxCode: null
  });

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Initialize GSM number from first attendee or userInfo
  useEffect(() => {
    if (attendees.length > 0 && attendees[0].phone) {
      setCardData(prev => ({ ...prev, gsmNumber: attendees[0].phone }));
    } else if (userInfo.phone) {
      setCardData(prev => ({ ...prev, gsmNumber: userInfo.phone }));
    }
  }, [attendees, userInfo.phone]);

  const setCardFieldValue = (field: CardField, value: string) => {
    setCardData((prev) => {
      if (prev[field] === value) {
        return prev;
      }

      return {
        ...prev,
        [field]: value
      };
    });
  };

  const setBillingFieldValue = (field: BillingField, value: string) => {
    setBillingData((prev) => {
      if (prev[field] === value) {
        return prev;
      }

      return {
        ...prev,
        [field]: value
      };
    });
  };

  const normalizeGsmNumber = (value: string) => {
    const country = countryPhoneCodes.find(c => c.code === selectedCountryCode);
    const maxLength = getMaxLengthForCountry(country);
    let digitsOnly = value.replace(/\D/g, '');

    // Strip country code prefix if browser autofilled full international number
    const codeDigits = selectedCountryCode.replace(/\D/g, '');
    if (codeDigits && digitsOnly.startsWith(codeDigits) && digitsOnly.length > maxLength) {
      digitsOnly = digitsOnly.slice(codeDigits.length);
    }

    return digitsOnly.slice(0, maxLength);
  };

  const normalizeCardNumber = (value: string) => {
    return formatCardNumber(value).replace(/\s/g, '');
  };

  const setFieldValue = (field: PaymentField, value: string) => {
    switch (field) {
      case 'cardHolderName':
      case 'expireMonth':
      case 'expireYear':
        setCardFieldValue(field, value);
        return;
      case 'cardNumber':
        setCardFieldValue(field, normalizeCardNumber(value));
        return;
      case 'cvc':
        setCardFieldValue(field, value.replace(/\D/g, '').slice(0, 4));
        return;
      case 'gsmNumber': {
        const normalizedValue = normalizeGsmNumber(value);
        setCardFieldValue(field, normalizedValue);
        return;
      }
      case 'identityNo':
      case 'postalCode':
        setBillingFieldValue(field, value.replace(/\D/g, ''));
        return;
      case 'address':
      case 'city':
      case 'country':
      case 'taxCode':
        setBillingFieldValue(field, value);
        return;
      default:
        return;
    }
  };

  const handleInputChange = (field: PaymentField, value: string) => {
    setFieldValue(field, value);
  };

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 16);
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
    return formatted.slice(0, 19); // Max 16 digits + 3 spaces
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue('cardNumber', e.target.value);
  };

  const syncAutofilledFields = () => {
    (Object.entries(fieldRefs.current) as [PaymentField, HTMLInputElement | HTMLSelectElement | null][])
      .forEach(([field, input]) => {
        if (!input) return;
        setFieldValue(field, input.value);
      });
  };

  const scheduleAutofillSync = useAutofillSync(syncAutofilledFields, totalPrice);


  // Prevent automatic scrolling when inputs are focused
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    // Use requestAnimationFrame to ensure proper timing
    requestAnimationFrame(() => {
      const input = e.target;
      if (input) {
        // Check if element is already in viewport
        const rect = input.getBoundingClientRect();
        const isInView = (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
        
        // Only scroll if element is not fully in view, and use 'nearest' to minimize scroll
        if (!isInView) {
          input.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      }
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate billing fields (always required)
    if (!billingData.address || !billingData.address.trim()) {
      newErrors.address = getTranslation('events.thisFieldIsRequired') || 'Address is required';
    }
    if (!billingData.city || !billingData.city.trim()) {
      newErrors.city = getTranslation('events.thisFieldIsRequired') || 'City is required';
    }
    if (!billingData.country || !billingData.country.trim()) {
      newErrors.country = getTranslation('events.thisFieldIsRequired') || 'Country is required';
    }
    if (!billingData.postalCode || !billingData.postalCode.trim()) {
      newErrors.postalCode = getTranslation('events.thisFieldIsRequired') || 'Postal code is required';
    }

    // Validate card fields (only if totalPrice > 0)
    if (totalPrice > 0) {
      // Identity number is required for paid tickets
      if (!billingData.identityNo || !billingData.identityNo.trim()) {
        newErrors.identityNo = getTranslation('events.thisFieldIsRequired') || 'Identity number is required';
      }
      
      if (!cardData.cardHolderName || !cardData.cardHolderName.trim()) {
        newErrors.cardHolderName = getTranslation('events.thisFieldIsRequired') || 'Card holder name is required';
      }
      
      if (!cardData.cardNumber || !cardData.cardNumber.trim()) {
        newErrors.cardNumber = getTranslation('events.thisFieldIsRequired') || 'Card number is required';
      } else if (!isValidCardNumber(cardData.cardNumber)) {
        newErrors.cardNumber = getTranslation('events.invalidCardNumber') || 'Invalid card number';
      }
      
      if (!cardData.expireMonth || !cardData.expireYear) {
        newErrors.expiry = getTranslation('events.thisFieldIsRequired') || 'Expiry date is required';
      } else {
        // Validate month is between 01-12
        const month = parseInt(cardData.expireMonth, 10);
        if (isNaN(month) || month < 1 || month > 12) {
          newErrors.expiry = getTranslation('events.invalidExpiryFormat') || 'Invalid format. Use MM / YYYY';
        } else {
          // Check if year is valid (not empty and reasonable)
          if (!cardData.expireYear || cardData.expireYear.length !== 4) {
            newErrors.expiry = getTranslation('events.invalidExpiryFormat') || 'Invalid format. Use MM / YYYY';
          }
        }
      }
      
      if (!cardData.cvc || !cardData.cvc.trim()) {
        newErrors.cvc = getTranslation('events.thisFieldIsRequired') || 'CVC is required';
      } else if (!isValidCVV(cardData.cvc)) {
        newErrors.cvc = getTranslation('events.invalidCvv') || 'Invalid CVV';
      }
      
      if (!cardData.gsmNumber || !cardData.gsmNumber.trim()) {
        newErrors.gsmNumber = getTranslation('events.phoneRequired') || 'Phone number is required';
      } else {
        // Use selected country code for validation
        const country = countryPhoneCodes.find(c => c.code === selectedCountryCode);
        if (country) {
          const validation = isValidPhoneNumberForCountry(cardData.gsmNumber, country);
          if (!validation.isValid) {
            newErrors.gsmNumber = validation.error || getTranslation('events.phoneError') || 'Invalid phone number';
          }
        } else {
          const validation = isValidPhoneNumberWithCountry(cardData.gsmNumber, selectedCountryCode);
          if (!validation.isValid) {
            newErrors.gsmNumber = validation.error || getTranslation('events.phoneError') || 'Invalid phone number';
          }
        }
      }
    }

    // Validate email
    if (!userInfo.email || !userInfo.email.trim()) {
      newErrors.email = getTranslation('events.emailRequired') || 'Email is required';
    } else if (!isValidEmail(userInfo.email)) {
      newErrors.email = getTranslation('events.emailError') || 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if all required fields are filled (for button disabled state)
  const isFormValid = (): boolean => {
    // Check billing fields
    if (!billingData.address?.trim() || !billingData.city?.trim() || 
        !billingData.country?.trim() || !billingData.postalCode?.trim()) {
      return false;
    }

    // Check card fields if paid
    if (totalPrice > 0) {
      if (!billingData.identityNo?.trim()) {
        return false;
      }
      if (!cardData.cardHolderName?.trim() || !cardData.cardNumber?.trim() || 
          !cardData.expireMonth || !cardData.expireYear || !cardData.cvc?.trim() || 
          !cardData.gsmNumber?.trim()) {
        return false;
      }
      // Validate formats
      if (!isValidCardNumber(cardData.cardNumber)) {
        return false;
      }
      if (!isValidCVV(cardData.cvc)) {
        return false;
      }
      
      // Validate phone number with country-based validation
      if (cardData.gsmNumber && cardData.gsmNumber.trim()) {
        const country = countryPhoneCodes.find(c => c.code === selectedCountryCode);
        if (country) {
          const validation = isValidPhoneNumberForCountry(cardData.gsmNumber, country);
          if (!validation.isValid) {
            return false;
          }
        } else {
          const validation = isValidPhoneNumberWithCountry(cardData.gsmNumber, selectedCountryCode);
          if (!validation.isValid) {
            return false;
          }
        }
      } else {
        return false;
      }
    }

    // Check email
    if (!userInfo.email?.trim() || !isValidEmail(userInfo.email)) {
      return false;
    }

    return true;
  };

  const pollPaymentStatus = async (pid: string) => {
    try {
      const response = await Services.ListenSHPayment(pid);
      
      if (response.data.status === "1") {
        // Payment successful
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        setShow3DS(false);
        
        // Prepare billing object according to guide - exact structure
        const billing = {
          tckn: billingData.identityNo || '',
          address: billingData.address,
          city: billingData.city,
          region: billingData.country, // region is country name (same as country)
          postalCode: billingData.postalCode,
          taxCode: billingData.taxCode || '',
          country: billingData.country,
          phone: cardData.gsmNumber || userInfo.phone || '',
          email: userInfo.email
        };
        
        onPaymentSuccess(pid, billing);
      } else if (response.data.status === "2") {
        // Payment failed
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        setShow3DS(false);
        onPaymentError(response.data.message || getTranslation('events.paymentFailed'));
      } else if (response.data.status === "5") {
        // Payment pending - continue polling
        // Do nothing, keep polling
      } else {
        // Other error
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        setShow3DS(false);
        onPaymentError(response.data.message || getTranslation('events.paymentFailed'));
      }
    } catch (error: any) {
      // Continue polling on network errors
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Show errors when button is clicked
    setShowErrors(true);
    
    if (!validateForm()) {
      // Scroll to first error
      setTimeout(() => {
        const firstErrorField = document.querySelector(`.${styles.formFieldError} input, .${styles.inputError}`);
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    setIsProcessing(true);

    // Ensure totalPrice is a valid number (match reference implementation - use number directly)
    const numericTotalPrice = typeof totalPrice === 'number' 
      ? totalPrice 
      : typeof totalPrice === 'string' 
        ? parseFloat(totalPrice) || 0
        : Number(totalPrice) || 0;
    
    // Convert to string for API (match reference - they pass number but API expects string)
    const priceString = numericTotalPrice.toString();

    // Prepare payment items
    const items = attendees.map(attendee => ({
      title: attendee.ticketTitle,
      type: "VIRTUAL",
      price: attendee.ticketPrice.toString()
    }));

    // Get payment currency (use TL as-is)
    const getPaymentCurrency = (displayCurrency: string): string => {
      const upperCurrency = (displayCurrency || 'TL').toUpperCase();
      return upperCurrency;
    };

    // Prepare payment request - exactly as per guide
    const paymentRequest = {
      userID: userID,
      cardHolderName: cardData.cardHolderName,
      cardNumber: cardData.cardNumber.replace(/\s/g, ''),
      expireMonth: cardData.expireMonth,
      expireYear: cardData.expireYear,
      cvc: cardData.cvc,
      saveCard: false,
      name: userInfo.name,
      lastname: userInfo.lastname,
      gsmNumber: cardData.gsmNumber || userInfo.phone || '',
      email: userInfo.email,
      identityNo: billingData.identityNo || (currency === 'TL' ? '11111111111' : '22222222222'),
      address: billingData.address,
      city: billingData.city,
      country: billingData.country,
      postalCode: billingData.postalCode,
      price: priceString,
      paidPrice: priceString,
      locale: 'EN',
      currency: getPaymentCurrency(currency),
      paymentGroup: 'PRODUCT',
      installment: 1,
      items: items
    };

    try {
      const response = await Services.InitializeSHPayment(paymentRequest);

      // Check if response.data is a string (backend PHP fatal error HTML)
      const responseData = response.data as any;
      if (typeof responseData === 'string') {
        const errorMsg = responseData.includes('Fatal error') 
          ? getTranslation('events.paymentInitializationFailed') + ': Backend configuration error'
          : getTranslation('events.paymentInitializationFailed');
        onPaymentError(errorMsg);
        setIsProcessing(false);
        return;
      }

      // Handle both string "1" and boolean true for success status
      const isSuccess = responseData.status === "1" || responseData.status === true || responseData.status === 1;
      
      if (isSuccess && responseData.htmlContent && responseData.paymentID) {
        setHtmlContent(responseData.htmlContent);
        setShow3DS(true);

        // Start polling payment status (match reference - poll every 1 second)
        pollingIntervalRef.current = setInterval(() => {
          pollPaymentStatus(responseData.paymentID!);
        }, 1000); // Poll every 1 second
      } else {
        // Check if it's a success response but missing required fields
        if (isSuccess && (!responseData.htmlContent || !responseData.paymentID)) {
          onPaymentError(getTranslation('events.paymentInitializationFailed') + ': Missing payment data');
        } else {
          const errorMessage = responseData.message || 
            (typeof responseData.status === 'string' ? responseData.status : String(responseData.status)) || 
            getTranslation('events.paymentInitializationFailed');
          onPaymentError(errorMessage);
        }
        setIsProcessing(false);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || getTranslation('events.paymentInitializationFailed');
      onPaymentError(errorMessage);
      setIsProcessing(false);
    }
  };

  if (show3DS && htmlContent) {
    return (
      <iframe
        ref={iframeRef}
        srcDoc={htmlContent}
        title="3D Secure"
        style={{
          width: "100vw",
          height: "100vh",
          border: "none",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 9999,
          backgroundColor: '#fff'
        }}
      />
    );
  }

  return (
    <Card className={styles.paymentCard}>
      <form
        onSubmit={handleSubmit}
        onFocusCapture={scheduleAutofillSync}
        onBlurCapture={scheduleAutofillSync}
        className={styles.paymentForm}
      >
        {totalPrice > 0 && (
          <div className={styles.formSection}>
            <h4 className={styles.sectionTitle}>{getTranslation('events.cardInformation')}</h4>
            <div className={styles.formGrid}>
              <div className={`${styles.formField} ${showErrors && errors.cardHolderName ? styles.formFieldError : ''}`}>
                <label className={showErrors && errors.cardHolderName ? styles.formLabelError : ''}>
                  {getTranslation('events.cardHolderName')} *
                </label>
                <input
                  type="text"
                  value={cardData.cardHolderName}
                  ref={(element) => {
                    fieldRefs.current.cardHolderName = element;
                  }}
                  onChange={(e) => handleInputChange('cardHolderName', e.target.value)}
                  onFocus={handleInputFocus}
                  placeholder={getTranslation('events.cardHolderName')}
                  name="cc-name"
                  autoComplete="cc-name"
                  className={showErrors && errors.cardHolderName ? styles.inputError : ''}
                />
                {showErrors && errors.cardHolderName && (
                  <span className={styles.errorMessage}>{errors.cardHolderName}</span>
                )}
              </div>
              <div className={`${styles.formField} ${showErrors && errors.cardNumber ? styles.formFieldError : ''}`}>
                <label className={showErrors && errors.cardNumber ? styles.formLabelError : ''}>
                  {getTranslation('events.cardNumber')} *
                </label>
                <input
                  type="text"
                  value={formatCardNumber(cardData.cardNumber)}
                  ref={(element) => {
                    fieldRefs.current.cardNumber = element;
                  }}
                  onChange={handleCardNumberChange}
                  onFocus={handleInputFocus}
                  placeholder="1234 5678 9012 3456"
                  maxLength={19}
                  name="cc-number"
                  autoComplete="cc-number"
                  className={showErrors && errors.cardNumber ? styles.inputError : ''}
                />
                {showErrors && errors.cardNumber && (
                  <span className={styles.errorMessage}>{errors.cardNumber}</span>
                )}
              </div>
              <div className={`${styles.formField} ${showErrors && errors.expiry ? styles.formFieldError : ''}`}>
                <label className={showErrors && errors.expiry ? styles.formLabelError : ''}>
                  {getTranslation('events.expiryMonth')} / {getTranslation('events.expiryYear')} *
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    value={cardData.expireMonth}
                    ref={(element) => {
                      fieldRefs.current.expireMonth = element;
                    }}
                    onChange={(e) => handleInputChange('expireMonth', e.target.value)}
                    onFocus={handleInputFocus}
                    name="cc-exp-month"
                    autoComplete="cc-exp-month"
                    className={showErrors && errors.expiry ? styles.inputError : ''}
                    style={{ flex: 1 }}
                  >
                    <option value="">{getTranslation('events.month')}</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <option key={month} value={month.toString().padStart(2, '0')}>
                        {month.toString().padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                  <select
                    value={cardData.expireYear}
                    ref={(element) => {
                      fieldRefs.current.expireYear = element;
                    }}
                    onChange={(e) => handleInputChange('expireYear', e.target.value)}
                    onFocus={handleInputFocus}
                    name="cc-exp-year"
                    autoComplete="cc-exp-year"
                    className={showErrors && errors.expiry ? styles.inputError : ''}
                    style={{ flex: 1 }}
                  >
                    <option value="">{getTranslation('events.year')}</option>
                    {Array.from({ length: 20 }, (_, i) => new Date().getFullYear() + i).map(year => (
                      <option key={year} value={year.toString()}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                {showErrors && errors.expiry && (
                  <span className={styles.errorMessage}>{errors.expiry}</span>
                )}
              </div>
              <div className={`${styles.formField} ${showErrors && errors.cvc ? styles.formFieldError : ''}`}>
                <label className={showErrors && errors.cvc ? styles.formLabelError : ''}>
                  {getTranslation('events.cvc')} *
                </label>
                <input
                  type="text"
                  value={cardData.cvc}
                  ref={(element) => {
                    fieldRefs.current.cvc = element;
                  }}
                  onChange={(e) => handleInputChange('cvc', e.target.value.replace(/\D/g, '').slice(0, 4))}
                  onFocus={handleInputFocus}
                  placeholder="123"
                  maxLength={4}
                  name="cc-csc"
                  autoComplete="cc-csc"
                  className={showErrors && errors.cvc ? styles.inputError : ''}
                />
                {showErrors && errors.cvc && (
                  <span className={styles.errorMessage}>{errors.cvc}</span>
                )}
              </div>
              <div className={`${styles.formField} ${showErrors && errors.gsmNumber ? styles.formFieldError : ''}`}>
                <label className={showErrors && errors.gsmNumber ? styles.formLabelError : ''}>
                  {getTranslation('events.phone')} (GSM) *
                </label>
                <div className={styles.phoneInputWrapper}>
                  <CountryCodeSelector
                    selectedCountryCode={selectedCountryCode}
                    onSelect={(country: CountryPhoneCode) => {
                      setSelectedCountryCode(country.code);
                      // Truncate phone number if it exceeds new country's max length
                      const maxLength = getMaxLengthForCountry(country);
                      if (cardData.gsmNumber.length > maxLength) {
                        setCardData(prev => ({ ...prev, gsmNumber: prev.gsmNumber.slice(0, maxLength) }));
                      }
                    }}
                  />
                  <input
                    type="tel"
                    ref={(element) => {
                      fieldRefs.current.gsmNumber = element;
                    }}
                    autoComplete="tel-national"
                    value={cardData.gsmNumber}
                    onChange={(e) => handleInputChange('gsmNumber', e.target.value)}
                    onFocus={handleInputFocus}
                    placeholder={getTranslation('events.phone')}
                    name="tel"
                    maxLength={getMaxLengthForCountry(countryPhoneCodes.find(c => c.code === selectedCountryCode))}
                    className={`${showErrors && errors.gsmNumber ? styles.inputError : ''} ${styles.phoneInput}`}
                  />
                </div>
                {showErrors && errors.gsmNumber && (
                  <span className={styles.errorMessage}>{errors.gsmNumber}</span>
                )}
              </div>
            </div>
          </div>
        )}

        <div className={styles.formSection}>
          <h4 className={styles.sectionTitle}>{getTranslation('events.billingInformation')}</h4>
          <div className={styles.formGrid}>
            <div className={`${styles.formField} ${showErrors && errors.identityNo ? styles.formFieldError : ''}`}>
              <label className={showErrors && errors.identityNo ? styles.formLabelError : ''}>
                {getTranslation('events.identityNumber')} {totalPrice > 0 ? '*' : `(${getTranslation('events.optional')})`}
              </label>
              <input
                type="text"
                value={billingData.identityNo}
                ref={(element) => {
                  fieldRefs.current.identityNo = element;
                }}
                onChange={(e) => handleInputChange('identityNo', e.target.value.replace(/\D/g, ''))}
                onFocus={handleInputFocus}
                placeholder={getTranslation('events.identityNumber')}
                name="identity-no"
                autoComplete="off"
                className={showErrors && errors.identityNo ? styles.inputError : ''}
              />
              {showErrors && errors.identityNo && (
                <span className={styles.errorMessage}>{errors.identityNo}</span>
              )}
            </div>
            <div className={`${styles.formField} ${showErrors && errors.address ? styles.formFieldError : ''}`}>
              <label className={showErrors && errors.address ? styles.formLabelError : ''}>
                {getTranslation('events.address')} *
              </label>
              <input
                type="text"
                value={billingData.address}
                ref={(element) => {
                  fieldRefs.current.address = element;
                }}
                onChange={(e) => handleInputChange('address', e.target.value)}
                onFocus={handleInputFocus}
                placeholder={getTranslation('events.addressFormat') || 'Street Address, Apartment'}
                name="street-address"
                autoComplete="street-address"
                className={showErrors && errors.address ? styles.inputError : ''}
              />
              {showErrors && errors.address && (
                <span className={styles.errorMessage}>{errors.address}</span>
              )}
            </div>
            <div className={`${styles.formField} ${showErrors && errors.city ? styles.formFieldError : ''}`}>
              <label className={showErrors && errors.city ? styles.formLabelError : ''}>
                {getTranslation('events.city')} *
              </label>
              <input
                type="text"
                value={billingData.city}
                ref={(element) => {
                  fieldRefs.current.city = element;
                }}
                onChange={(e) => handleInputChange('city', e.target.value)}
                onFocus={handleInputFocus}
                placeholder={getTranslation('events.cityFormat') || 'City Name'}
                name="address-level2"
                autoComplete="address-level2"
                className={showErrors && errors.city ? styles.inputError : ''}
              />
              {showErrors && errors.city && (
                <span className={styles.errorMessage}>{errors.city}</span>
              )}
            </div>
            <div className={`${styles.formField} ${showErrors && errors.country ? styles.formFieldError : ''}`}>
              <label className={showErrors && errors.country ? styles.formLabelError : ''}>
                {getTranslation('events.country')} *
              </label>
              <input
                type="text"
                value={billingData.country}
                ref={(element) => {
                  fieldRefs.current.country = element;
                }}
                onChange={(e) => handleInputChange('country', e.target.value)}
                onFocus={handleInputFocus}
                placeholder={getTranslation('events.regionFormat') || 'Region/State'}
                name="country-name"
                autoComplete="country-name"
                className={showErrors && errors.country ? styles.inputError : ''}
              />
              {showErrors && errors.country && (
                <span className={styles.errorMessage}>{errors.country}</span>
              )}
            </div>
            <div className={`${styles.formField} ${showErrors && errors.postalCode ? styles.formFieldError : ''}`}>
              <label className={showErrors && errors.postalCode ? styles.formLabelError : ''}>
                {getTranslation('events.postalCode')} *
              </label>
              <input
                type="text"
                value={billingData.postalCode}
                ref={(element) => {
                  fieldRefs.current.postalCode = element;
                }}
                onChange={(e) => handleInputChange('postalCode', e.target.value.replace(/\D/g, ''))}
                onFocus={handleInputFocus}
                placeholder="XXXXX"
                name="postal-code"
                autoComplete="postal-code"
                className={showErrors && errors.postalCode ? styles.inputError : ''}
              />
              {showErrors && errors.postalCode && (
                <span className={styles.errorMessage}>{errors.postalCode}</span>
              )}
            </div>
            <div className={styles.formField}>
              <label>{getTranslation('events.taxCode')} ({getTranslation('events.optional')})</label>
              <input
                type="text"
                value={billingData.taxCode}
                ref={(element) => {
                  fieldRefs.current.taxCode = element;
                }}
                onChange={(e) => handleInputChange('taxCode', e.target.value)}
                onFocus={handleInputFocus}
                placeholder={getTranslation('events.taxCode')}
                name="tax-code"
                autoComplete="off"
              />
            </div>
          </div>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="large"
          disabled={isProcessing || !isFormValid()}
          className={styles.submitButton}
        >
          {isProcessing ? getTranslation('events.processing') : getTranslation('events.payNow')}
        </Button>
      </form>
    </Card>
  );
};

export default PaymentForm;
